import React, { useEffect, useState, useContext } from "react";
import FaDisposalSchedule from "../../../components/Schedules/CA/FaDisposal/FaDisposalSchedule";
import { AuthContext } from "../../../contexts/AuthContext";
import { getCaSettings } from "../../../builders/settings/CaSetting";
import { useSuccessDialog } from "../../../contexts/SuccessDialogContext";
import { useErrorDialog } from "../../../contexts/ErrorDialogContext";
import ComponentLoading from "../../../components/App/ComponentLoading";
import { useAccessControl } from "../../../utils/hooks/useAccessControl";
import { CA_SCHEDULES } from "../../../utils/data/controlData";

const getRegisteredDisposalSourceId = (row, index) =>
  `fa-register-disposal-${row.assetId || row.rowKey || index}`;

const getRegisteredDisposalCategoryId = (row, index) =>
  row.categoryId ||
  row.assetCategoryId ||
  `fa-register-category-${String(row.assetCategory || row.category || index)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")}`;

const getRegisteredDisposalCategoryAssetId = (categoryId) =>
  `fa-register-disposal-category-${categoryId}`;

const buildRegisteredDisposalSources = (
  faRegisterImport,
  existingSource = {},
) => {
  const registeredRows =
    faRegisterImport?.faDisposal ||
    faRegisterImport?.summaries?.faDisposal ||
    [];

  const existingDetails = [
    ...(existingSource.disposalDetails || []),
    ...(Array.isArray(existingSource.categories)
      ? existingSource.categories.flatMap(
          (category) => category.disposalDetails || [],
        )
      : []),
  ];

  const existingBySourceId = existingDetails.reduce((map, row) => {
    const sourceId = row.registeredAssetSourceId || row.assetId;
    if (sourceId) {
      map[sourceId] = row;
    }
    return map;
  }, {});

  const categoryMap = new Map();
  const disposalDetails = registeredRows.map((row, index) => {
    const registeredAssetSourceId = getRegisteredDisposalSourceId(row, index);
    const categoryId = getRegisteredDisposalCategoryId(row, index);
    const assetCategory =
      row.assetCategory || row.category || "Registered Assets";
    const existingRow = existingBySourceId[registeredAssetSourceId] || {};

    const detail = {
      registeredAssetSourceId,
      assetId: registeredAssetSourceId,
      categoryId,
      assetCategoryId: categoryId,
      assetCategory,
      category: assetCategory,
      isImportedFromFARegister: true,
      faRegisterSource: true,
      faRegisterSourceDetail: true,
      locked: true,
      description: row.descriptionOfAsset || "",
      CaName: row.caName || "",
      CaSummary: "",
      isHirePurchase: false,
      yaOfPurchase: row.yaOfPurchase || "",
      isFullyClaimed: existingRow.isFullyClaimed ?? row.fullyClaimed ?? false,
      noOfYearsBf: row.noOfYearsBf ?? 0,
      sourceNoOfYearsBf: row.noOfYearsBf ?? 0,
      costInDetails: Number(row.cost || 0),
      hpPrincipalPaid: 0,
      twdvBf: row.twdvBf === "N.A." ? 0 : Number(row.twdvBf || 0),
      salesProceeds: existingRow.salesProceeds || 0,
      netBookValue: existingRow.netBookValue || 0,
      bcBa: 0,
      disposalGainLoss: 0,
    };

    if (!categoryMap.has(categoryId)) {
      categoryMap.set(categoryId, {
        source: "FA Register",
        assetId: getRegisteredDisposalCategoryAssetId(categoryId),
        categoryId,
        assetCategoryId: categoryId,
        assetCategory,
        isImportedFromFARegister: true,
        faRegisterSource: true,
        locked: true,
        disposalDetails: [],
      });
    }

    categoryMap.get(categoryId).disposalDetails.push(detail);
    return detail;
  });

  const categories = Array.from(categoryMap.values()).map((category) => {
    const disposal = category.disposalDetails.reduce(
      (total, detail) => total + Number(detail.costInDetails || 0),
      0,
    );

    return {
      ...category,
      cost: { disposal: -disposal },
    };
  });

  return {
    source: "FA Register",
    assetId: "registered-assets",
    assetCategory: "Registered Assets",
    cost: {
      disposal: -disposalDetails.reduce(
        (total, detail) => total + Number(detail.costInDetails || 0),
        0,
      ),
    },
    disposalDetails,
    categories,
    assetCategories: faRegisterImport?.assetCategories || [],
  };
};

const FaDisposalSchedulePage = ({ company, scheduleId }) => {
  const { getCAScheduleDataByType, updateCASchedule, deleteCAScheduleByType } =
    useContext(AuthContext);
  const [saveTrigger, setSaveTrigger] = useState(false);
  const [schedule, setSchedule] = useState({});
  const [scheduleDetails, setScheduleDetails] = useState({});

  const { canEdit: canEditCASchedule } = useAccessControl(CA_SCHEDULES);

  const [loading, setLoading] = useState(true);

  const handleError = useErrorDialog();
  const handleSuccess = useSuccessDialog();

  const saveSchedule = async (schedule, scheduleDetails) => {
    if (!company || !scheduleId) {
      handleError("Missing UEN or Schedule ID. Please try again.");
      return;
    }

    const updatedSchedule = {
      sch_id: scheduleId,
      scheduleType: "FADisposals",
      dataRows: scheduleDetails.dataRows,
      registeredAssetSourceRows: scheduleDetails.registeredAssetSourceRows,
      showNetbookValue: scheduleDetails.showNetbookValue,
      TWDVSummary: scheduleDetails.TWDVSummary,
      BCBASummary: scheduleDetails.BCBASummary,
      doc: scheduleDetails.doc,
      notes: schedule.notes,
      reviewNotes: schedule.reviewNotes,
    };

    setLoading(true);
    const response = await updateCASchedule(updatedSchedule);
    setLoading(false);

    if (response) {
      handleSuccess("The schedule has been saved successfully.");
      setSaveTrigger((prev) => !prev);
    } else {
      handleError(
        "There was a problem saving the schedule. Please contact support if the issue persists.",
      );
    }
  };

  const deleteSchedule = async () => {
    if (!scheduleId) {
      handleError("Missing Schedule ID. Please try again.");
      return;
    }

    const updatedSchedule = {
      sch_id: scheduleId,
    };

    setLoading(true);
    const response = await deleteCAScheduleByType(updatedSchedule);
    setLoading(false);

    if (response) {
      handleSuccess("Schedule deleted successfully.");
      localStorage.removeItem("selectedSection");
      localStorage.setItem("selectedComponent", "Detailed P&L");
      window.location.reload();
    } else {
      handleError(
        "There was a problem saving the schedule. Please contact support if the issue persists.",
      );
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        // Fetch schedule data from API
        const response = await getCAScheduleDataByType(
          scheduleId,
          "FADisposals",
        );
        const scheduleData = response?.schedule;

        let caSettings = getCaSettings();

        if (scheduleData) {
          setSchedule({
            id: scheduleId,
            type: "Fixed Asset Disposal",
            scheduleNo: scheduleData.scheduleNo,
            name: "Fixed Asset Disposal",
            caSettings: scheduleData.caSetting || caSettings,
            notes: scheduleData.notes || "",
            reviewNotes: scheduleData.reviewNotes || "",
            refSources: scheduleData.scheduleMapping || {},
          });

          const registeredAssetSources = buildRegisteredDisposalSources(
            scheduleData.faRegisterImport,
            scheduleData.registeredAssetSourceRows,
          );

          setScheduleDetails({
            s19dClaims: scheduleData.s19dClaims || [],
            dataRows: scheduleData.dataRows || [],
            registeredAssetSources,
            disposedHpAssets: scheduleData.hirePurchaseAssets || [],
            hirePurchaseSummary: scheduleData.hirePurchaseSummary,
            disposalSchSettings: {
              showNetbookValue: scheduleData.showNetbookValue || false,
            },
          });
        }
      } catch (error) {
        handleError(error);
      } finally {
        setLoading(false);
      }
    };

    if (company && scheduleId) {
      fetchData();
    }
  }, [saveTrigger, scheduleId, company.uen]);

  return (
    <>
      {loading ? (
        <ComponentLoading />
      ) : (
        <FaDisposalSchedule
          company={company}
          schedule={schedule}
          scheduleDetails={scheduleDetails}
          saveSchedule={saveSchedule}
          deleteSchedule={deleteSchedule}
          canEditSchedule={canEditCASchedule}
        />
      )}
    </>
  );
};

export default FaDisposalSchedulePage;
